// src/features/system/developer/components/video-gallery/prewarm.ts
// ────────────────────────────────────────────────────────────────────────────
// TikTok / Shorts-grade buffer-warm pipeline for the vertical video feed.
//
// Two-stage pre-warm:
//
//   Stage 1 (always on) — pre-sign playback URLs for the N+1, N+2 slides
//     as soon as the active index changes. `requestPlayback` is already
//     single-flight + cached in React-Query, so calling it eagerly costs
//     at most one BFF round-trip per slide. Outcome: when the user swipes
//     down, the next slide has a URL ready instantly (no spinner gap).
//
//   Stage 2 (network-aware) — fire a small HTTP Range request for the
//     first ~512 KB of the next slide's signed URL. This seeds the
//     browser's HTTP cache so the <video> element finds bytes already
//     resident and skips the "Loading…" placeholder entirely.
//
// Network awareness:
//   • The Network Information API (`navigator.connection`) is queried
//     before Stage 2. On 2G / slow-2G we skip range pre-warm entirely
//     (would steal bandwidth from the active slide). On `save-data` we
//     do the same.
//   • Stage 1 is cheap (BFF roundtrip only) and runs unconditionally.
//
// Single-flight + cancellation:
//   • Range pre-warm requests are tracked in a Map keyed by video_id so
//     a rapid swipe doesn't queue up duplicate background fetches.
//   • Each in-flight request carries its AbortController so callers can
//     cancel speculative pre-warms when the user scrolls past.
//
// Structured logging:
//   • `console.debug('[prewarm] stage1', ...)` on hits/misses — toggle
//     with `localStorage.setItem('debug:prewarm', '1')`.
// ────────────────────────────────────────────────────────────────────────────

import type { VideoFeedItem, PlaybackUrlResponse } from './types';
import { getPrewarmTier, recordSample } from './bandwidthEstimator';

type PrewarmFetchInit = RequestInit & {
  // Chromium fetch priority hint; ignored by browsers that do not support it.
  priority?: 'high' | 'low' | 'auto';
};

// Hard cap on a single prewarm — even on gigabit Wi-Fi we never block
// more than this. Acts as a circuit breaker against a misbehaving tier
// or a giant header. The estimator picks the requested bytes; this is
// the absolute ceiling.
const RANGE_PREWARM_MAX_BYTES = 8 * 1024 * 1024;  // 8 MB hard ceiling
const RANGE_PREWARM_TIMEOUT   = 8_000;            // hard cap so a stalled prewarm cannot block forever

/** In-flight range pre-warm tracker, keyed by video_id. */
const inFlightRangePrewarm = new Map<string, AbortController>();

/** Tiny debug helper — opt-in via localStorage flag, zero overhead otherwise. */
function dbg(...args: unknown[]): void {
  try {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem('debug:prewarm') !== '1') return;
    // eslint-disable-next-line no-console
    console.debug('[prewarm]', ...args);
  } catch { /* localStorage may be unavailable in privacy modes */ }
}

/**
 * Network Information API helper. Returns `true` when the network can
 * comfortably afford a 512 KB speculative fetch without harming the
 * active stream.
 *
 * Truth-table (defensive defaults: assume "good" when the API is missing):
 *   • No `connection` API at all       → true   (desktop, Safari)
 *   • `saveData = true`                → false  (user-requested data saver)
 *   • effectiveType ∈ {slow-2g, 2g}    → false
 *   • effectiveType = 3g + downlink<1  → false  (very slow 3G edge cases)
 *   • everything else                  → true
 */
export function canAffordRangePrewarm(): boolean {
  try {
    const nav = navigator as unknown as {
      connection?: {
        effectiveType?: string;
        saveData?: boolean;
        downlink?: number;
      };
    };
    const c = nav.connection;
    if (!c) return true;
    if (c.saveData) return false;
    const eff = c.effectiveType || '';
    if (eff === 'slow-2g' || eff === '2g') return false;
    if (eff === '3g' && typeof c.downlink === 'number' && c.downlink < 1) return false;
    return true;
  } catch {
    return true;  // unknown API shape → optimistic
  }
}

/**
 * Stage 1 — pre-sign playback URLs for upcoming slides.
 *
 * Called whenever the active slide index changes. Pre-signs slides
 * `[active+1, active+lookahead]` so when the user swipes the URL is
 * already minted (zero spinner gap on swipe).
 *
 * Idempotent: `requestPlayback` is single-flight + cached, so calling
 * it repeatedly for the same video is free.
 */
export function presignUpcoming(
  items: VideoFeedItem[],
  activeIndex: number,
  requestPlayback: (videoId: string) => Promise<PlaybackUrlResponse>,
  lookahead = 3,
): void {
  for (let i = 1; i <= lookahead; i++) {
    const next = items[activeIndex + i];
    if (!next) break;
    // Skip rows that have no playable surface (e.g. orphaned uploads).
    if (next.error === 'upload_orphan' || next.error === 'metadata_unavailable') continue;
    requestPlayback(next.video_id)
      .then(() => dbg('stage1.hit', { idx: activeIndex + i, vid: next.video_id }))
      .catch((err) => dbg('stage1.miss', { idx: activeIndex + i, err: String(err?.message || err) }));
  }
}

/**
 * Stage 2 — range pre-warm the first ~512 KB of the next slide.
 *
 * Issues a single bounded Range request against the signed playback URL.
 * Seeds the browser's HTTP cache so the `<video>` element finds bytes
 * already resident and starts playing without going through "Loading…".
 *
 * Skipped on:
 *   • Slow networks (2G / save-data) — see `canAffordRangePrewarm`.
 *   • Already in flight for the same video_id (deduped via Map).
 *   • Items with terminal errors (`upload_orphan`, `metadata_unavailable`).
 *
 * Safety:
 *   • Hard 4 s timeout — a stalled prewarm cannot block anything.
 *   • AbortController exposed via `cancelRangePrewarm` for swipe-past.
 */
export async function rangePrewarmNext(
  items: VideoFeedItem[],
  activeIndex: number,
  requestPlayback: (videoId: string) => Promise<PlaybackUrlResponse>,
): Promise<void> {
  if (!canAffordRangePrewarm()) {
    dbg('stage2.skip', { reason: 'network' });
    return;
  }
  // Adaptive policy: ask the bandwidth estimator how many bytes to
  // warm and how many slides ahead. The first call after page load
  // returns a connection-floor-based default; subsequent calls use
  // measured throughput (EMA over recent prewarm + progress samples).
  const tier = getPrewarmTier();
  const bytes = Math.min(tier.bytes, RANGE_PREWARM_MAX_BYTES);
  dbg('stage2.tier', { mbps: tier.mbps.toFixed(2), bytes, lookahead: tier.lookahead });

  for (let i = 1; i <= tier.lookahead; i++) {
    // Sequential (not Promise.all) so the closer slide gets bytes first.
    // Each call is bounded by RANGE_PREWARM_TIMEOUT so a stall on N+1
    // cannot block N+2 forever.
    // eslint-disable-next-line no-await-in-loop
    await rangePrewarmOne(items, activeIndex + i, requestPlayback, bytes);
  }
}

/** Range-prewarm a single slide at `index`. Internal helper for `rangePrewarmNext`. */
async function rangePrewarmOne(
  items: VideoFeedItem[],
  index: number,
  requestPlayback: (videoId: string) => Promise<PlaybackUrlResponse>,
  bytes: number,
): Promise<void> {
  const next = items[index];
  if (!next) return;
  if (next.error === 'upload_orphan' || next.error === 'metadata_unavailable') return;
  if (inFlightRangePrewarm.has(next.video_id)) return;

  // Pre-sign first (Stage 1 covers this but the call is idempotent and
  // we need the URL synchronously here).
  let pb: PlaybackUrlResponse;
  try {
    pb = await requestPlayback(next.video_id);
  } catch (err) {
    dbg('stage2.signfail', { vid: next.video_id, err: String((err as Error)?.message || err) });
    return;
  }
  if (!pb?.playback_url) return;

  const ctl = new AbortController();
  inFlightRangePrewarm.set(next.video_id, ctl);
  const t = setTimeout(() => ctl.abort('prewarm-timeout'), RANGE_PREWARM_TIMEOUT);
  const startedAt = performance.now();

  try {
    // HEAD-style range probe: request just the leading bytes. The
    // browser parses Content-Range and stores the chunk in HTTP cache
    // so the subsequent `<video>` request hits it instantly.
    const init: PrewarmFetchInit = {
      method: 'GET',
      headers: { Range: `bytes=0-${bytes - 1}` },
      signal: ctl.signal,
      // No credentials — signed URLs are bearer-by-URL.
      credentials: 'omit',
      // Hint to the browser this is speculative.
      priority: 'low',
    };
    const res = await fetch(pb.playback_url, init);
    // Drain the body so the browser HTTP cache stores it AND so we can
    // observe the real byte count for the throughput estimate. Without
    // draining, Chrome may abandon the buffer on socket reuse.
    let received = 0;
    if (res.body) {
      const reader = res.body.getReader();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        if (value) received += value.byteLength;
      }
    } else {
      try {
        const buf = await res.arrayBuffer();
        received = buf.byteLength;
      } catch { /* ignore */ }
    }
    const elapsedMs = performance.now() - startedAt;
    if (received > 0 && elapsedMs > 0) recordSample(received, elapsedMs);
    dbg('stage2.hit', {
      idx: index, vid: next.video_id, bytes: received, ms: Math.round(elapsedMs),
    });
  } catch (err) {
    // Abort or network error — both are benign at this stage.
    dbg('stage2.miss', { idx: index, vid: next.video_id, err: String((err as Error)?.name || err) });
  } finally {
    clearTimeout(t);
    inFlightRangePrewarm.delete(next.video_id);
  }
}

/**
 * Cancel any in-flight range pre-warm for a given video. Called when
 * the user scrolls past the speculatively-warmed slide and the bytes
 * are no longer worth pulling.
 */
export function cancelRangePrewarm(videoId: string): void {
  const ctl = inFlightRangePrewarm.get(videoId);
  if (!ctl) return;
  ctl.abort('prewarm-stale');
  inFlightRangePrewarm.delete(videoId);
}
